import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import NachhaltigkeitAssistent from "@/components/NachhaltigkeitAssistent";
import { applySeo } from "@/lib/seo";
import { CONFIG } from "@/config/nachhaltigkeitAssistent";
import { ChevronRight } from "lucide-react";

export default function CalculatorNachhaltigkeitspraeferenz() {
  useEffect(() => {
    applySeo({
      title: "Nachhaltigkeitspräferenz-Assistent | Grüne Geldanlage",
      description:
        "Kostenloses Werkzeug: Beantworten Sie die Nachhaltigkeitspräferenz-Fragen Ihrer Bank vorab und erhalten Sie einen PDF-Ausdruck für Ihr Beratungsgespräch. Keine Anlageberatung.",
      canonicalPath: "/rechner/nachhaltigkeitspraeferenz-assistent",
    });
    const setMeta = (sel, content) => {
      const m = document.querySelector(sel);
      if (m) m.setAttribute("content", content);
    };
    setMeta('meta[property="og:title"]', "Nachhaltigkeitspräferenz-Assistent – Beratungsgespräch vorbereiten");
    setMeta('meta[property="og:description"]', "PDF-Ausdruck der eigenen Nachhaltigkeitspräferenz für das Beratungsgespräch – kostenlos, ohne Speicherung.");
    setMeta('meta[name="twitter:title"]', "Nachhaltigkeitspräferenz-Assistent");
    setMeta('meta[name="twitter:description"]', "Bereiten Sie Ihr Beratungsgespräch zur Nachhaltigkeitspräferenz vor.");
  }, []);

  const jsonLdApp = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Nachhaltigkeitspräferenz-Assistent",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    url: "https://gruene-geldanlage.com/rechner/nachhaltigkeitspraeferenz-assistent",
    inLanguage: "de-DE",
    description:
      "Kostenloses Werkzeug zur Vorbereitung des Beratungsgesprächs zur Nachhaltigkeitspräferenz nach EU-Recht. Erzeugt einen PDF-Ausdruck.",
  };
  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: CONFIG.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const jsonLdBreadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: "https://gruene-geldanlage.com/" },
      { "@type": "ListItem", position: 2, name: "Rechner", item: "https://gruene-geldanlage.com/rechner/nachhaltigkeits-rendite" },
      { "@type": "ListItem", position: 3, name: "Nachhaltigkeitspräferenz-Assistent", item: "https://gruene-geldanlage.com/rechner/nachhaltigkeitspraeferenz-assistent" },
    ],
  };

  return (
    <div className="min-h-screen bg-brand-paper" data-testid="calc-praeferenz-page">
      <Header />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdApp) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaq) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumbs) }} />

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <nav className="text-xs text-neutral-500 mb-6" aria-label="Brotkrumen">
          <Link to="/" className="hover:text-brand-primary">Start</Link>
          <ChevronRight size={12} className="inline mx-1" />
          <Link to="/rechner/nachhaltigkeits-rendite" className="hover:text-brand-primary">Rechner</Link>
          <ChevronRight size={12} className="inline mx-1" />
          <span className="text-neutral-700">Nachhaltigkeitspräferenz-Assistent</span>
        </nav>
        <div className="overline mb-4">Werkzeug · Sechster Rechner</div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-tight tracking-tight text-brand-dark mb-6 hyphens-auto" lang="de">
          Nachhaltigkeitspräferenz-Assistent: Ihr Beratungsgespräch vorbereiten.
        </h1>
        <p className="text-lg text-neutral-700 max-w-3xl leading-relaxed">
          Seit dem 2. August 2022 müssen Banken und Anlageberater Ihre Nachhaltigkeitspräferenz
          abfragen. Dieses Werkzeug führt Sie in rund fünf Minuten durch die relevanten Fragen und
          erzeugt einen PDF-Ausdruck, den Sie mit ins Gespräch nehmen können. Es empfiehlt keine
          Produkte und ersetzt keine Anlageberatung.
        </p>
        <p className="text-xs text-neutral-500 mt-4">{CONFIG.meta.displayStand}</p>
      </section>

      {/* Wizard */}
      <section className="pb-16" data-testid="wizard-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <NachhaltigkeitAssistent />
        </div>
      </section>

      {/* Editorial Content — 800+ Wörter */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16">
        <div className="prose-editorial max-w-none">
          <h2>Was ist die Nachhaltigkeitspräferenz-Abfrage?</h2>
          <p>
            Seit dem 2. August 2022 sind Wertpapierdienstleister in der Europäischen Union
            verpflichtet, im Rahmen der Geeignetheitsprüfung nach MiFID II auch die
            Nachhaltigkeitspräferenzen ihrer Kundinnen und Kunden zu erheben. Die Vorgabe steht in
            der Delegierten Verordnung (EU) 2017/565, geändert durch die Delegierte Verordnung
            (EU) 2021/1253. Praktisch heißt das: Bevor Ihre Bank Ihnen einen Fonds oder ein
            anderes Wertpapier empfiehlt, muss sie Sie fragen, ob und wie Sie Nachhaltigkeit
            berücksichtigt haben möchten – und darf danach nur Produkte anbieten, die zu diesen
            Angaben passen.
          </p>
          <p>
            Die Praxis sieht häufig anders aus. Fragebogen sind knapp, Beraterinnen und Berater
            haben nur begrenzte Zeit, und viele Anlegerinnen und Anleger wissen nicht, was
            „Taxonomie", „SFDR" oder „PAI" konkret meint. Die Folge: Nachhaltigkeitswünsche werden
            entweder pauschal mit „Ja" beantwortet – ohne konkrete Vorgaben – oder gleich mit
            „Nein", weil die Kategorien zu abstrakt wirken. Unser Assistent bereitet Sie darauf vor,
            damit Sie das Gespräch souverän führen können.
          </p>

          <h2>Die drei gesetzlichen Kriterien erklärt</h2>
          <h3>1. EU-Taxonomie</h3>
          <p>
            Die EU-Taxonomie ist ein einheitliches Klassifikationssystem für ökologisch nachhaltige
            Wirtschaftstätigkeiten. Eine Aktivität gilt dann als taxonomiekonform, wenn sie zu einem
            von sechs EU-Umweltzielen wesentlich beiträgt (z. B. Klimaschutz oder Kreislaufwirtschaft),
            gleichzeitig keinem anderen Ziel erheblich schadet und Mindestschutz-Kriterien (etwa
            Menschenrechte) erfüllt. Sie können in Ihrer Präferenz einen Mindestanteil
            taxonomiekonformer Investitionen fordern. Wichtig zu wissen: Bislang gilt nur ein
            kleiner Teil der Wirtschaft als taxonomiekonform. Hohe Mindestanteile schränken die
            Produktauswahl deshalb stark ein.
          </p>
          <h3>2. Nachhaltige Investitionen nach SFDR</h3>
          <p>
            Nach Artikel 2 Nr. 17 der EU-Offenlegungsverordnung (SFDR) gelten Investitionen als
            „nachhaltig", wenn sie zu einem ökologischen oder sozialen Ziel beitragen, keinem Ziel
            erheblich schaden und die begünstigten Unternehmen gute Governance einhalten. Die
            Definition ist breiter als die Taxonomie – deshalb liegen die ausgewiesenen Anteile in
            der Praxis meist höher. Sie können in Ihrer Präferenz einen Mindestanteil solcher
            Investitionen fordern und optional festlegen, dass der Schwerpunkt eher ökologisch oder
            eher sozial liegen soll.
          </p>
          <h3>3. Berücksichtigung der wichtigsten nachteiligen Auswirkungen (PAI)</h3>
          <p>
            PAI steht für „Principal Adverse Impacts" – die wichtigsten nachteiligen Auswirkungen
            einer Investition auf Nachhaltigkeitsfaktoren wie Treibhausgasemissionen, Biodiversität,
            Wasserverbrauch, soziale Standards oder Menschenrechte. Sie können verlangen, dass ein
            Produkt PAI aktiv berücksichtigt – und dabei bestimmte Themen priorisiert. Wie ein
            Anbieter PAI konkret berücksichtigt (Ausschluss, Engagement, Best-in-Class), ist ihm
            überlassen.
          </p>

          <h2>Warum die Artikel-8-/Artikel-9-Einstufung nicht reicht</h2>
          <p>
            Fonds werden nach der Offenlegungsverordnung in drei Kategorien einsortiert: Artikel 6
            (ohne Nachhaltigkeitsbezug), Artikel 8 (Fonds mit ökologischen oder sozialen Merkmalen)
            und Artikel 9 (Fonds mit Nachhaltigkeit als explizitem Anlageziel). Diese Kategorien
            geben Auskunft darüber, wie ein Anbieter <em>selbst</em> seinen Fonds klassifiziert. Sie
            sagen aber wenig darüber aus, ob der Fonds Ihre <em>konkrete</em>
            Nachhaltigkeitspräferenz erfüllt. Ein Artikel-8-Fonds kann einen Mindestanteil
            nachhaltiger Investitionen von 0 % ausweisen – rechtlich möglich, für Ihre Präferenz
            aber möglicherweise unzureichend. Deshalb formuliert der Gesetzgeber die drei
            Kriterien oben so explizit: Sie brauchen quantifizierte Mindestanteile oder eine
            aktive PAI-Berücksichtigung, nicht bloß ein Etikett.
          </p>
          <p>
            Zur Einordnung: Laut FNG-Marktbericht 2026 (Stichtag 31.12.2025) kombinieren rund 77 %
            der erfassten nachhaltigen Publikumsfonds in Deutschland PAI-Berücksichtigung mit
            Angaben nach der Offenlegungsverordnung. Die Taxonomie spielt als eigenständiges
            Kriterium bislang kaum eine Rolle, weil nur ein geringer Anteil wirtschaftlicher
            Aktivitäten als taxonomiekonform gilt. Details in unserem{" "}
            <Link to="/nachhaltige-geldanlage-deutschland">Marktreport zur nachhaltigen
            Geldanlage in Deutschland</Link>.
          </p>

          <h2>Was Sie im Gespräch sagen können</h2>
          <p>
            Der Assistent baut aus Ihren Angaben einen Musterabsatz in der Ich-Form, den Sie
            wortwörtlich verwenden können. Zusätzlich schlägt er Fragen an Ihre Beraterin oder
            Ihren Berater vor. Zwei Beispiele: „Wie hoch ist der tatsächliche Anteil
            taxonomiekonformer Investitionen und wie wird er berechnet?" – „Was passiert, wenn das
            Produkt meine Vorgaben später nicht mehr erfüllt?" Beides sind Fragen, auf die ein
            seriöser Berater eine belastbare Antwort geben können sollte.
          </p>

          <h2>Häufige Fehler und Greenwashing-Fallen</h2>
          <p>
            Der klassische Fehler: „Ja, ich möchte nachhaltig anlegen" – ohne konkrete Vorgaben. Ihr
            Berater kann dann nahezu jeden Artikel-8-Fonds als „geeignet" verkaufen, weil Ihre
            Präferenz nicht quantifiziert ist. Ein zweiter Fehler: Sie akzeptieren einen sehr hohen
            Mindestanteil (z. B. 80 % Taxonomie), obwohl der Markt das nicht hergibt – Sie
            bekommen dann faktisch keine Empfehlung und weichen auf ein „Standard"-Produkt aus.
            Ein dritter Fehler: Ausschlusslisten werden vage formuliert. „Keine Waffen" ist
            weniger präzise als „Keine Umsätze aus kontroversen Waffen, Umsatzschwelle 5 %".
            Praktische Hinweise zum Erkennen von Marketing-Kosmetik finden Sie in unserem
            Artikel{" "}
            <Link to="/artikel/greenwashing-erkennen">Greenwashing erkennen</Link>.
          </p>

          <h2>Ausblick: SFDR 2.0</h2>
          <p>
            Die EU-Kommission überarbeitet aktuell die Offenlegungsverordnung. Geplant sind drei
            neue Kategorien: „Transition" (Fonds, die den Übergang zur nachhaltigen Wirtschaft
            finanzieren), „ESG-Basic" (Fonds mit ESG-Merkmalen) und „Sustainable" (Fonds mit
            konkretem Nachhaltigkeitsziel). Damit könnten sich die Bezeichnungen ändern, mit denen
            Sie in wenigen Jahren konfrontiert werden. Die grundsätzliche Logik der drei Kriterien
            – Taxonomie, nachhaltige Investitionen, PAI – bleibt aber Teil der Präferenz-Erhebung.
            Mehr Kontext dazu:{" "}
            <Link to="/artikel/sfdr-2-0-reform">SFDR 2.0 – Was sich mit der Reform ändert</Link>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16" data-testid="calc-faq">
        <div className="overline mb-3">Häufige Fragen</div>
        <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight mb-8">FAQ zur Nachhaltigkeitspräferenz</h2>
        <div className="space-y-4">
          {CONFIG.faq.map((f, i) => (
            <details key={i} className="group border border-neutral-200 bg-white p-6 open:border-brand-primary">
              <summary className="cursor-pointer font-serif text-lg font-medium text-brand-dark list-none flex justify-between items-center">
                <span>{f.q}</span>
                <span className="text-brand-primary group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-4 text-neutral-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Disclaimer/Privacy */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16">
        <div className="border border-neutral-200 bg-neutral-50 p-6 text-sm text-neutral-700 leading-relaxed space-y-3" data-testid="page-disclaimer">
          <div><strong>Datenschutz:</strong> {CONFIG.privacy}</div>
          <div><strong>Hinweis:</strong> {CONFIG.disclaimer}</div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
